import { Link, useNavigate } from 'react-router-dom';
import { Gauge, Fuel, Cog, Scale, Eye, Clock, CheckCircle, Bitcoin, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCompare } from '@/context/CompareContext';
import { getEngineImage } from '@/utils/imageUtils';

// Condition badge colors
const conditionColors = {
  'New': 'bg-emerald-500',
  'Like New': 'bg-sky-500',
  'Rebuilt': 'bg-blue-500',
  'Good Used': 'bg-amber-500',
  'High Hour': 'bg-orange-600',
};

export default function EngineCard({ engine, showCondition = false }) {
  const { addToCompare, isInCompare, removeFromCompare } = useCompare();
  const navigate = useNavigate();
  const inCompare = isInCompare(engine.id);

  const handleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inCompare) {
      removeFromCompare(engine.id);
    } else {
      addToCompare(engine);
    }
  };

  const handleBuyNow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/checkout/${engine.id}`);
  };

  return (
    <Link 
      to={`/engine/${engine.id}`} 
      className="engine-card block group"
      data-testid={`engine-card-${engine.id}`}
    >
      <div className="relative aspect-video overflow-hidden bg-slate-100">
        <img
          src={getEngineImage(engine)}
          alt={engine.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          {engine.condition && (
            <span className={`${conditionColors[engine.condition] || 'bg-slate-500'} text-white text-xs font-bold px-2 py-1 rounded-sm`}>
              {engine.condition}
            </span>
          )}
          {engine.is_new && !engine.condition && <span className="badge-new">New</span>}
          {engine.is_featured && <span className="badge-featured">Featured</span>}
          {engine.is_bestseller && <span className="badge-bestseller">Bestseller</span>}
        </div>
        <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant={inCompare ? "default" : "secondary"}
            size="sm"
            className={inCompare ? 'bg-orange-500 hover:bg-orange-600' : ''}
            onClick={handleCompare}
            data-testid={`compare-btn-${engine.id}`}
          >
            <Scale className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
          <span className="font-medium text-slate-900">{engine.brand}</span>
          <span>•</span>
          <span>{engine.model}</span>
          {engine.item_number && (
            <>
              <span>•</span>
              <span className="text-xs font-mono">#{engine.item_number}</span>
            </>
          )}
        </div>

        <h3 className="text-lg font-semibold text-slate-900 mb-3 line-clamp-2 group-hover:text-orange-500 transition-colors">
          {engine.title}
        </h3>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Gauge className="w-4 h-4 text-orange-500" />
            <span className="font-mono">{engine.horsepower?.toLocaleString()} HP</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Cog className="w-4 h-4 text-orange-500" />
            <span>{engine.rpm?.toLocaleString() || engine.rpm_range} RPM</span>
          </div>
          {engine.hours && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Clock className="w-4 h-4 text-orange-500" />
              <span className="font-mono text-xs">{engine.hours}</span>
            </div>
          )}
          {engine.tier_rating && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle className="w-4 h-4 text-orange-500" />
              <span className="text-xs">{engine.tier_rating}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          {engine.price ? (
            <span className="price-tag text-xl font-bold">${engine.price.toLocaleString()}</span>
          ) : (
            <span className="text-sm text-slate-500">Contact for price</span>
          )}
          {engine.price && (
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handleBuyNow}
              data-testid={`buy-btn-${engine.id}`}
            >
              <Bitcoin className="w-4 h-4 mr-1" />
              Buy Now
            </Button>
          )}
        </div>
      </div>
    </Link>
  );
}
